import { AlpineData, TSelectArray, TSelectList, TSelectObject, TSelectValue } from "@/js/interfaces/Components/alpinejs/SelectUnique";
import is from '@/js/app/support/is';
import { InvalidArgumentException } from '@/app/exceptions';
import { createAlpineLivewireComponent } from "@nolikein/typed-alpinejs";

/**
 * Le composant permet de gérer 2 cas, soit une liste sous forme d'array, soit une liste sous forme d'object.
 *
 * Pour un array [1, 2, 3] :
 * Labels affichés : 1, 2, 3
 * defaultValue reçu : 2 (la valeur)
 * Valeur envoyée à Livewire : 2 (la valeur)
 * En interne keyAsValue doit stocker l'index pour retrouver la valeur
 *
 * Pour un object {a: 1, b: 2, c: 3} :
 * Labels affichés : 1, 2, 3 (les valeurs de l'objet)
 * defaultValue reçu : 'b' (la clé)
 * Valeur envoyée à Livewire : 'b' (la clé)
 * En interne keyAsValue stocke la clé
 */

export default {
    name: 'SelectUnique',
    component: (
        livewireData: string,
        initList: TSelectList = [],
        defaultValue: TSelectValue = '',
        valueIfEmpty: null | TSelectValue = null,
        disabled: boolean = false,
    ) => createAlpineLivewireComponent<AlpineData, Record<string, unknown>, object>({
        open: false,
        search: '',
        keyAsValue: '',
        defaultValue: '',
        list: initList,
        livewireData: livewireData,
        disabled: disabled,
        isListArray: false,

        init() {
            // Sanitize the list
            if (is.array(initList)) {
                this.isListArray = true;
                // Pour un array, defaultValue est la valeur, on doit trouver son index
                if ((is.number(defaultValue) || is.string(defaultValue)) && defaultValue !== '') {
                    const arrayKey = this.getArrayKey(initList, defaultValue);
                    this.keyAsValue = arrayKey;
                    this.defaultValue = arrayKey;
                } else {
                    this.keyAsValue = '';
                    this.defaultValue = '';
                }

            } else if (is.object(initList)) {
                // Pour un object, defaultValue est déjà la clé
                if ((is.number(defaultValue) || is.string(defaultValue)) && defaultValue !== '') {
                    this.keyAsValue = defaultValue;
                    this.defaultValue = defaultValue;
                } else {
                    this.keyAsValue = '';
                    this.defaultValue = '';
                }

            } else {
                throw new InvalidArgumentException('The given list MUST be either an array or a Javascript object');
            }

            this.$watch('keyAsValue', (keyAsValue: string) => {
                this.keyAsValue = keyAsValue;
                this.publish();
            });
        },

        getList(): TSelectList {
            if ('' === this.search) {
                return this.list;
            }

            const filtered: TSelectList = this.isListArray ? [] : {};
            if (this.isListArray) {
                (this.list as TSelectArray).forEach((item) => {
                    const itemStr = is.number(item) ? item.toString() : item;
                    if (itemStr.toLowerCase().includes(this.search.toLowerCase())) {
                        (filtered as TSelectArray).push(item);
                    }
                });
            } else {
                Object.entries(this.list as TSelectObject).forEach(([key, item]) => {
                    const itemStr = is.number(item) ? item.toString() : item;
                    if (itemStr.toLowerCase().includes(this.search.toLowerCase())) {
                        (filtered as TSelectObject)[key] = item;
                    }
                });
            }
            return filtered;
        },

        /**
         * Detect if should add or remove a keyAsValue
         */
        assignOrRemoveValue(val: TSelectValue) {
            if (this.keyAsValue !== val) {
                this.assignValue(val);
            } else {
                this.removeValue();
            }
        },

        /**
         * Add a keyAsValue in the select bag
         */
        assignValue(val: TSelectValue): void {
            this.keyAsValue = val;
            this.open = false;
        },

        /**
         * Remove a keyAsValue from the select bag
         */
        removeValue(): void {
            this.keyAsValue = '';
        },

        /**
         * Checks if a keyAsValue is already selected
         */
        isSelected(val: TSelectValue): boolean {
            return this.keyAsValue === val;
        },

        /**
         * Update our keyAsValue into livewire (the backend)
         */
        publish(): void {
            if (!this.disabled) {
                this.$wire.$set(this.livewireData, this.isSelectionEmpty() ? valueIfEmpty : this.getValue());
            }
        },

        /**
         * Reset the values bag.
         */
        reset(): void {
            this.keyAsValue = this.defaultValue;
        },

        /**
         * Toggle the display of the the selection menu.
         */
        toggleDisplaySelectionMenu(): void {
            if (!this.disabled) {
                this.open = !this.open;

                // Remove search on open/close
                this.search = '';

                // Focus the search input if exists
                if (undefined !== this.$refs.searchInput) {
                    this.$nextTick(() => {
                        this.$refs.searchInput.focus();
                    });
                }
            }
        },

        /**
         * Tells the action to do when the form input is enabled.
         */
        onEnable(): void {
            this.disabled = false;

        },

        /**
         * Tells the action to do when the form input is disabled.
         */
        onDisable(): void {
            this.disabled = true;
        },

        /**
         * Retrieve the input text.
         */
        getValueLabel(): string {
            const value = this.isListArray
                ? (this.list as TSelectArray)[this.keyAsValue as number]
                : (this.list as TSelectObject)[this.keyAsValue];
            // Transtype the value to have only string
            return is.number(value) ? value.toString() : value;
        },

        /**
         * Retrieve the input value.
         */
        getValue(): TSelectValue {
            return this.isListArray
                ? (this.list as TSelectArray)[this.keyAsValue as number]
                : this.keyAsValue;
        },

        /**
         * Checks if the component selection is empty.
         */
        isSelectionEmpty(): boolean {
            return '' === this.keyAsValue;
        },

        /**
         * Get array index from given value
         */
        getArrayKey(list: TSelectArray, value: TSelectValue): number {
            const arrayKey = list.indexOf(value);
            if (-1 !== arrayKey) {
                return arrayKey;
            }

            throw new InvalidArgumentException('The given default value does not exist in the list provided');
        },
    }),
};
